import type { ScoringConfig } from '@league/shared/scoring';
import type { SessionSummary } from '@league/shared/contract';
import type { DirectorLifecycleResult } from '@league/shared/league-lifecycle';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useParams } from 'react-router-dom';
import { LINK_CLASS } from '@league/ui';

import { fetchDirectorLeagues, fetchLeague, LeagueHeader } from './LeagueHeader';
import { useDirectorLifecycle } from './useDirectorLifecycle';

/**
 * THE SPINE OF THE DIRECTOR APP. Ten tabs, in this order, per UI-UX-SPEC §3.3.
 *
 * The order is not alphabetical and not by how finished each one is — it is the order a league
 * actually happens in. Roster before Seating before Scores before Standings, then the things that
 * surround a league session, then Settings last because it is where you go when something is wrong
 * rather than when something is due.
 *
 * **Settings is the tenth and the spec's mockups omit it.** It is required anyway, and the roster
 * lock is the proof: the lock date has to be changeable from somewhere, and "somewhere" was nowhere.
 *
 * Tabs that are not built resolve to a bracket naming the phase rather than 404ing. The shell is
 * what is being judged here, and a tab strip whose items lead nowhere cannot be walked.
 */

export interface League {
  scoringConfig: ScoringConfig;
  id: string;
  name: string;
  slug: string;
  status: string;
  weekday: number;
  playerCap: number;
  rosterLockAt: string | null;
  /** True once a lock date was typed rather than derived — see `leagues.rosterLockManual`. */
  rosterLockManual: boolean;
  /** The market's zone. Every date this workspace shows is formatted in it — see `lib/when.ts`. */
  timeZone: string;
  marketId: string;
  venueId: string | null;
  venueName: string | null;
  /** What a seat costs, in cents. Null until she sets one. */
  seatPriceCents: number | null;
  /** Players pay through the platform rather than paying her directly. */
  playersPayOnline: boolean;
  /** The kinds of email this league does not send — see `EMAIL_SWITCHES`. */
  emailsOff: string[];
  /** Players enrolled — how an instructor, who has no roster, sees how many are playing. */
  enrolledCount: number;
}

/**
 * ⚠ NOT DESCRIBED HERE. `SessionSummary` is the API's own declaration of this shape — the
 * repository returns it and this imports it, so a projection change is a compile error at both ends
 * in one commit. There used to be a hand-written copy in this file carrying five of its fields;
 * that is exactly the arrangement that let `registered` render nowhere for a fortnight.
 */
type Session = SessionSummary;

export interface LeagueContext {
  league: League;
  sessions: Session[];
  lifecycle: DirectorLifecycleResult | undefined;
  refresh: () => void;
}

export const WEEKDAYS = [
  'Sundays',
  'Mondays',
  'Tuesdays',
  'Wednesdays',
  'Thursdays',
  'Fridays',
  'Saturdays',
] as const;

export function LeagueWorkspace() {
  const { id } = useParams<{ id: string }>();

  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['league', id],
    queryFn: () => fetchLeague(id!),
    enabled: Boolean(id),
  });

  const directorLeagues = useQuery({
    queryKey: ['director-leagues'],
    queryFn: fetchDirectorLeagues,
    enabled: Boolean(id),
  });
  const directorLeague = directorLeagues.data?.leagues.find((league) => league.id === id);
  const [lifecycle] = useDirectorLifecycle(
    query.data && directorLeague
      ? [{ status: query.data.league.status, sessions: directorLeague.lifecycleSessions }]
      : [],
  );

  if (query.isPending) {
    return <p className="px-6 py-10 text-sm text-ink-3">Loading…</p>;
  }

  if (query.error || !query.data) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-10">
        <p className="text-sm text-ink-2">That league isn&rsquo;t here.</p>
        <Link to="/director/leagues" className={`mt-3 inline-block text-[13px] ${LINK_CLASS}`}>
          ← All leagues
        </Link>
      </div>
    );
  }

  const { league, sessions } = query.data;
  const context: LeagueContext = { league, sessions, lifecycle, refresh: () => {
    void query.refetch();
    void queryClient.invalidateQueries({ queryKey: ['director-leagues'] });
    void queryClient.invalidateQueries({ queryKey: ['director-dashboard'] });
  } };

  return (
    <div className="mx-auto flex max-w-4xl flex-col px-6 py-10">
      <LeagueHeader leagueId={league.id} />

      <div className="mt-8">
        {/* Keyed by league: moving straight to another league rebuilds its tabs, so no form keeps the last league's values (2026-10-09). */}
        <Outlet key={league.id} context={context} />
      </div>
    </div>
  );
}
